// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/**
 * data.ts — load waste.json + meta.json and expose the cube as row objects with
 * a small set of memoised reducers. Every view derives its aggregates from here
 * so there is one source of truth. The cube is a few thousand rows, so plain
 * array reduces are fast enough and simpler than pre-baking every rollup.
 */
import type { Meta, Row, SRow, WasteJson, ViewId } from './types';
import { isRecovery, CATEGORIES } from './domain';

export interface Dataset {
  meta: Meta;
  years: string[];
  latestYear: string;
  firstYear: string;
  catKeys: string[];
  jurisCodes: string[];
  fateKeys: string[];
  streamKeys: string[];
  rows: Row[];
  streamRows: SRow[];
  pops: Record<string, Record<string, number>>;
  target: { recoveryRate: number; year: number; label: string };
  /** Population of a jurisdiction (or all) in a given year, or null. */
  pop(year: string, code?: string): number | null;
}

let cache: Dataset | null = null;
let geoCache: unknown | null = null;

const base = import.meta.env.BASE_URL || '/';

export async function loadDataset(): Promise<Dataset> {
  if (cache) return cache;
  const [waste, meta] = await Promise.all([
    fetch(`${base}data/waste.json`).then((r) => { if (!r.ok) throw new Error('waste'); return r.json() as Promise<WasteJson>; }),
    fetch(`${base}data/meta.json`).then((r) => { if (!r.ok) throw new Error('meta'); return r.json() as Promise<Meta>; }),
  ]);
  cache = buildDataset(waste, meta);
  return cache;
}

export function buildDataset(w: WasteJson, meta: Meta): Dataset {
  const rows: Row[] = w.cube.map(([yi, ji, ci, fi, t]) => ({
    yr: w.years[yi], j: w.jurisCodes[ji], c: w.catKeys[ci], f: w.fateKeys[fi], t,
  }));
  const streamRows: SRow[] = w.streamCube.map(([yi, ji, si, fi, t]) => ({
    yr: w.years[yi], j: w.jurisCodes[ji], s: w.streamKeys[si], f: w.fateKeys[fi], t,
  }));
  return {
    meta,
    years: w.years,
    latestYear: w.years[w.years.length - 1],
    firstYear: w.years[0],
    catKeys: w.catKeys,
    jurisCodes: w.jurisCodes,
    fateKeys: w.fateKeys,
    streamKeys: w.streamKeys,
    rows,
    streamRows,
    pops: w.pops,
    target: w.target,
    pop(year, code) {
      const p = w.pops[year];
      if (!p) return null;
      if (code) return p[code] ?? null;
      return Object.values(p).reduce((a, b) => a + b, 0);
    },
  };
}

// ── Reducers over category rows ──────────────────────────────────────────────
export interface RowFilter { yr?: string; j?: string; c?: string; f?: string }
export function filterRows(rows: Row[], f: RowFilter): Row[] {
  return rows.filter(
    (r) => (f.yr == null || r.yr === f.yr) && (f.j == null || r.j === f.j) && (f.c == null || r.c === f.c) && (f.f == null || r.f === f.f),
  );
}
export function total(rows: Row[]): number { return rows.reduce((a, r) => a + r.t, 0); }
export function recovered(rows: Row[]): number { return rows.reduce((a, r) => a + (isRecovery(r.f) ? r.t : 0), 0); }
export function recoveryRate(rows: Row[]): number { const t = total(rows); return t > 0 ? (recovered(rows) / t) * 100 : 0; }

/** Sum by an arbitrary key over a row subset → Map<key, tonnes>. */
export function sumBy<T extends Row | SRow>(rows: T[], keyFn: (r: T) => string): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) m.set(keyFn(r), (m.get(keyFn(r)) || 0) + r.t);
  return m;
}

// ── Common view aggregates ───────────────────────────────────────────────────
export interface MaterialAgg { key: string; total: number; rec: number; rate: number; byFate: Map<string, number> }

/** Per-material aggregate for a given year (national unless `juris` set). */
export function materialsForYear(d: Dataset, year: string, juris?: string): MaterialAgg[] {
  const sub = filterRows(d.rows, { yr: year, j: juris });
  return d.catKeys
    .map((key) => {
      const rr = sub.filter((r) => r.c === key);
      const t = total(rr);
      const rec = recovered(rr);
      return { key, total: t, rec, rate: t > 0 ? (rec / t) * 100 : 0, byFate: sumBy(rr, (r) => r.f) };
    })
    .filter((m) => m.total > 0)
    .sort((a, b) => b.total - a.total);
}

export interface StateAgg { code: string; total: number; rec: number; rate: number; perCapita: number | null; disposal: number }
export function statesForYear(d: Dataset, year: string): StateAgg[] {
  return d.jurisCodes.map((code) => {
    const rr = filterRows(d.rows, { yr: year, j: code });
    const t = total(rr);
    const rec = recovered(rr);
    const pop = d.pop(year, code);
    return {
      code,
      total: t,
      rec,
      rate: t > 0 ? (rec / t) * 100 : 0,
      perCapita: pop ? t / pop : null,
      disposal: t - rec,
    };
  });
}

/** National total tonnes per year. */
export function totalsByYear(d: Dataset): Array<{ yr: string; total: number; rate: number; perCapita: number | null }> {
  return d.years.map((yr) => {
    const rr = filterRows(d.rows, { yr });
    const t = total(rr);
    const pop = d.pop(yr);
    return { yr, total: t, rate: recoveryRate(rr), perCapita: pop ? t / pop : null };
  });
}

/** A single material's series across years (national unless juris). */
export function materialSeries(d: Dataset, cat: string, juris?: string): Array<{ yr: string; total: number; rate: number; byFate: Map<string, number> }> {
  return d.years.map((yr) => {
    const rr = filterRows(d.rows, { yr, c: cat, j: juris });
    return { yr, total: total(rr), rate: recoveryRate(rr), byFate: sumBy(rr, (r) => r.f) };
  });
}

// ── Stream reducers ──────────────────────────────────────────────────────────
export function streamTotal(rows: SRow[]): number { return rows.reduce((a, r) => a + r.t, 0); }
export function streamRecoveryRate(rows: SRow[]): number {
  const t = streamTotal(rows); if (!t) return 0;
  const rec = rows.reduce((a, r) => a + (isRecovery(r.f) ? r.t : 0), 0);
  return (rec / t) * 100;
}
export function streamsForYear(d: Dataset, year: string): Array<{ key: string; total: number; rate: number; byFate: Map<string, number> }> {
  return d.streamKeys.map((key) => {
    const rr = d.streamRows.filter((r) => r.yr === year && r.s === key);
    return { key, total: streamTotal(rr), rate: streamRecoveryRate(rr), byFate: sumBy(rr, (r) => r.f) };
  });
}

// ── Search (materials, states, fates) ────────────────────────────────────────
export interface SearchHit { kind: 'material' | 'state'; key: string; label: string; sub: string }
export function search(d: Dataset, q: string): SearchHit[] {
  const s = q.trim().toLowerCase();
  if (s.length < 2) return [];
  const hits: SearchHit[] = [];
  for (const c of CATEGORIES) {
    if (c.label.toLowerCase().includes(s) || c.short.toLowerCase().includes(s)) {
      hits.push({ kind: 'material', key: c.key, label: c.short, sub: 'Material' });
    }
  }
  for (const code of d.jurisCodes) {
    const nm = code.toLowerCase();
    if (nm.includes(s)) hits.push({ kind: 'state', key: code, label: code, sub: 'State / territory' });
  }
  // also match state full names
  return hits.slice(0, 10);
}

export async function loadGeo(): Promise<unknown> {
  if (geoCache) return geoCache;
  const res = await fetch(`${base}data/boundaries.geojson`);
  if (!res.ok) throw new Error('geo');
  geoCache = await res.json();
  return geoCache;
}

export type { ViewId };
